<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h2>Prodotti nel pacchetto "{{kit?.title.it}}"</h2>
        <v-btn color="primary" size="small" @click="$router.push(`/kits/${$route.params.id}/products/new`)">Nuovo</v-btn>
      </v-col>
      <v-col cols="12">
        <v-divider />
      </v-col>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" sm="6" md="4" lg="2">
            <v-text-field  v-model="query.title" label="Titolo" clearable></v-text-field>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <data-table ref="table" :headers="headers" collection="products" view-sub-path="" :viewable="false" :editable="false" deletable
                    :query="query" :query-filter="{kit:$route.params.id}" @error="onListError" >
          <template v-slot:item.title.it="{ item }">
            <a @click="$router.push(`/kits/${$route.params.id}/products/${item._id}`)" ><strong>{{ item.title.it }}</strong></a>
          </template>

          <template v-slot:item.price="{ item }">
            {{ item.price.toFixed(2) }}€<span v-if="item.second_price">, riacquisto: {{ item.second_price.toFixed(2) }}€</span>
          </template>
          <template v-slot:item.lessons="{ item }">
            <v-btn :disabled="item.is_seats_product" color="primary" variant="text" icon="mdi-run" v-on:click="$router.push(`/kits/${$route.params.id}/products/${item._id}/lessons`)"></v-btn>
          </template>
          <template v-slot:item.type="{ item }">
            <p class="mb-0">{{getProductTypeLabel(item.type)}}</p>
            <span v-if="item.type === 'distinct' && item.seats_has_same_expiration">(con scadenza condivisa)</span>
            <span v-if="item.type === 'distinct' && !item.seats_has_same_expiration">(con scadenze separate)</span>
          </template>
          <template v-slot:item.seats="{ item }">
            <div v-if="item.type === 'mixed'">
              <p class="mb-0">Tipologia sedute:
                <v-chip-group v-if="item.seats_types">
                  <v-chip size="small" density="compact" v-for="(s,i) in item.seats_types" :key="`${item._id}_seats_${i}`">{{getSeatsName(s)}}</v-chip>
                </v-chip-group>
              </p>
              <p v-if="item.consumable" class="mb-0">N° sedute: <strong>{{item.num_seats}}</strong></p>
              <p class="mb-0">Durata: <strong>{{item.seats_duration}} giorni</strong></p>
            </div>
            <div v-else-if="item.type === 'distinct'" >
              <div v-for="(s,i) in item.seats" style="border-bottom: 1px solid #ddd">
                <p class="mb-0">Tipologia sedute: <v-chip-group v-if="s.seats_types">
                  <v-chip size="small" density="compact" v-for="(st,j) in s.seats_types" :key="`${item._id}_seats_${i}_${j}`">{{getSeatsName(st)}}</v-chip>
                </v-chip-group></p>
                <p v-if="s.consumable" class="mb-0">N° sedute: <strong>{{s.num_seats}}</strong></p>
                <p class="mb-1">Durata: <strong>{{s.duration}} giorni</strong></p>
              </div>
            </div>
            <span v-else>-</span>
          </template>

          <template v-slot:item.clone="{ item }">
            <v-btn color="primary" variant="text" v-on:click="requestClone(item._id)" icon="mdi-content-copy"></v-btn>
          </template>

          <template #item.editproduct="{item}">
            <div class="d-flex w-100 justify-center">
              <v-btn variant="text" icon="mdi-pencil" color="primary" @click="$router.push(`/kits/${$route.params.id}/products/${item._id}`)"></v-btn>
            </div>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import {defineComponent, ref, computed, watch, onMounted, inject, onBeforeUnmount, useTemplateRef} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import utils from '../utils'
import DataTable from "@/components/DataTable.vue";
import {type DataTableError, type KitDisplay, type Product, type Query, SeatsType} from "@/models.ts";
import {type Messages, Msg} from "@/plugins/messages.ts";
import type {Auth} from "@/plugins/auth.ts";
import type {Globals} from "@/plugins/globals.ts";
import api from "@/api";
import {productTypeOptions} from "@/consts.ts";
import config from "@/config.ts";


export default defineComponent({
  components: {DataTable},
  setup() {
    const auth = inject('auth') as Auth
    const messages = inject('messages') as Messages
    const globals = inject('globals') as Globals
    const router = useRouter()
    const route = useRoute()

    const table = useTemplateRef<typeof DataTable>('table')
    const query = ref<Query>({})
    const kit = ref<KitDisplay|undefined>()

    let wh:any;
    onMounted(async ()=>{
      await auth.waitForLogin
      await globals.waitForInit
      await router.isReady()

      const res = await api.get<KitDisplay>('kits', route.params.id as string)
      kit.value = res.data

      /*
      await Promise.all([
        globals.refresh('categories'),
        globals.refresh('difficulties'),
      ])*/

      query.value = utils.getSessionQuery(`kits/${route.params.id}/products`)
      wh = watch(query.value, ()=>{
        utils.setSessionQuery(`kits/${route.params.id}/products`, query.value)
      }, { deep: true })
    })
    onBeforeUnmount(()=>{
      if(wh) wh();
    })
    return {
      table,
      kit,
      formatDateTime : utils.formatDateTime,
      query,
      headers : [
        { title : 'Titolo', sortable: true, align: 'left', value : 'title.it' },
        { title : 'Codice prodotto (SKU)', sortable: true, align: 'left', value : 'sku' },
        { title : 'Prezzo', sortable: true, align: 'left', value : 'price' },
        { title: 'Tipologia', sortable: false, align: 'left', value: 'type' },
        { title: 'Sedute', sortable: false, align: 'left', value: 'seats' },
        { title: 'Intervallo giorni', sortable: true, align: 'left', value: 'days_interval' },
        { title : 'Lezioni', sortable: false, align: 'center', value : 'lessons', width : 60 },
        { title : 'Clona', sortable: false, align: 'center', value : 'clone', width : 60 },
        { title : '', sortable: false, align: 'center', value : 'editproduct', width : 60 },
      ],
      onListError(e:DataTableError) {
        if(e.status === 401) {
          messages.error(Msg.permissionError)
          auth.logout()
          return
        }
        messages.error(`Errore: ${e.message}`)
      },
      getProductTypeLabel(pt:string) {

        const opt = utils.getObjectInArray(productTypeOptions,'value', pt)
        return opt ? opt.title : opt;
      },
      getSeatsName(s:SeatsType) {
        /*
        <v-chip small v-if="item.is_seats_product && item.seats_types && item.seats_types.includes('topgymexperience')">TopGym Experience</v-chip>
                      <v-chip small v-if="item.is_seats_product && item.seats_types && item.seats_types.includes('opengym')">Open Gym</v-chip>
                      <v-chip small v-if="item.is_seats_product && item.seats_types && item.seats_types.includes('corsi')">Sala Corsi</v-chip>
                      <v-chip small v-if="item.is_seats_product && item.seats_types && item.seats_types.includes('onetoone')">One-to-One</v-chip>
                      <v-chip small v-if="item.is_seats_product && item.seats_types && item.seats_types.includes('gym')">Ginnastica e Corpo Libero</v-chip>
                      <v-chip small v-if="item.is_seats_product && item.seats_types && item.seats_types.includes('vacu')">Vacu</v-chip>
         */
        switch (s) {
          case SeatsType.exp:
            return "Top Gym Experience"
          case SeatsType.opengym:
            return "Open Gym"
          case SeatsType.corsi:
            return "Sala corsi"
          case SeatsType.gym:
            return "Bike indoor"
          case SeatsType.vacu:
            return "Vacu"
        }
        return s
      },
      requestClone( id:string ) {
        messages.requestConfirm('Richiesta clonazione', 'Sei sicuro di voler duplicare questo elemento?', false,  async ( accepted )=>{
            if(accepted) {
              const res = await api.get('products', `${id}/duplicate`)
              if(!res.success) {
                messages.error(res.message ?? Msg.serverError)
              }
              await table.value!.loadItems()

            }
          });
      },
    }
  }
})
</script>
